import { CircleHelp } from 'lucide-react';
import Image from 'next/image';
import { useId, type CSSProperties } from 'react';

import { env } from '@ria-client/env';
import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import type { CtaType } from '@ria-client/segment/Analytics/types';
import { LinkType } from '@ria-client/segment/enums';
import { EntityPlacementCtaTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import type {
  BaseComparisonTable,
  BaseComparisonTableColumn,
} from '@ria-client/supergraph/review/schema/types';
import {
  CtaButton,
  DisclosureTooltip,
  NumericLayout,
  RiaDisclaimer,
  TrackedLink,
} from '@ria-client/tw-shared-ui';
import { ContentLink } from '../nodes/lib/content-link';

export interface ReviewComparisonTableProps {
  comparisonTable: NonNullable<BaseComparisonTable>;
}

const MAXIMUM_COLUMNS_MOBILE = 2;

export function ReviewComparisonTable({ comparisonTable }: ReviewComparisonTableProps) {
  const { columns, heading } = comparisonTable;
  const headingId = useId();

  if (!columns || columns.length < 2) {
    return null;
  }

  const hasRiaOffer = columns.some(column => isRiaOffer(column));
  const driverIds = [
    ...new Set(columns.flatMap(column => column.drivers.map(driver => driver.id).filter(Boolean))),
  ];
  // Every column renders one cell per row so subgrid can align them:
  // banner (non-RIA), logo, name, rating|-, divider, ...drivers, cta, disclaimer|-
  const rowCount = (hasRiaOffer ? 0 : 1) + 5 + driverIds.length;

  return (
    <section className="mb-8" aria-labelledby={heading ? headingId : undefined}>
      {heading ? (
        <h2 className="typography-text-large-bold mb-6" id={headingId}>
          {heading}
        </h2>
      ) : null}
      {hasRiaOffer ? (
        <span className="typography-body-small block bg-[var(--content-blue-lighter)] px-4 py-2 font-bold tracking-widest">
          ADVERTISEMENT
        </span>
      ) : null}
      <div
        className="grid grid-cols-[repeat(var(--mobile-columns),minmax(0,1fr))] sm:grid-cols-[repeat(var(--columns),minmax(0,1fr))]"
        style={
          {
            '--columns': columns.length,
            '--mobile-columns': Math.min(MAXIMUM_COLUMNS_MOBILE, columns.length),
            '--rows': rowCount,
          } as CSSProperties
        }
      >
        {columns.map((column, index) => (
          <ComparisonColumn
            column={column}
            columnIndex={index}
            columns={columns}
            driverIds={driverIds}
            hasRiaOffer={hasRiaOffer}
            key={column.marketplaceEntity.id}
          />
        ))}
      </div>
      {hasRiaOffer ? <ReviewComparisonEditorialDisclaimer /> : null}
    </section>
  );
}

interface ComparisonColumnProps {
  column: BaseComparisonTableColumn;
  columnIndex: number;
  columns: BaseComparisonTableColumn[];
  driverIds: string[];
  hasRiaOffer: boolean;
}

function ComparisonColumn({
  column,
  columnIndex,
  columns,
  driverIds,
  hasRiaOffer,
}: ComparisonColumnProps) {
  const { marketplaceEntity, isPrimaryEntity } = column;
  const analyticsPayload = {
    index: columnIndex,
    institutionId: marketplaceEntity.institution.id,
    offerId: marketplaceEntity.id,
    productId: marketplaceEntity.product.id,
    productType: marketplaceEntity.productType,
    monetizable: marketplaceEntity.monetizable,
  };
  const cta = getColumnCta(marketplaceEntity);
  const riaDisclaimerText = getRiaDisclaimerText(column);
  const isFidelityOffer = getIsFidelityOffer(column);
  const columnClassName = [
    columnIndex >= MAXIMUM_COLUMNS_MOBILE ? 'hidden sm:grid' : 'grid',
    'row-span-(--rows) grid-cols-1 grid-rows-subgrid',
    getColumnBorderClassName({ columnIndex, isPrimaryEntity, hasRiaOffer }),
  ].join(' ');

  return (
    <AnalyticsSkuViewed
      as="div"
      className={columnClassName}
      data-testid={`comparison-column-${columnIndex}`}
      additionalAttributes={{ page_type: 'review' }}
      impressionPosition={columnIndex}
      impressionSection="marketplace_comparison_table_product"
      institutionId={marketplaceEntity.institution.id}
      institutionName={marketplaceEntity.institution.name}
      level="offer"
      monetizable={marketplaceEntity.monetizable}
      name={marketplaceEntity.name}
      offerId={marketplaceEntity.id}
      offerName={marketplaceEntity.name}
      productId={marketplaceEntity.product.id}
      productName={marketplaceEntity.product.name}
      productType={marketplaceEntity.productType}
      source={SEGMENT_SKU_VIEWED_SOURCE}
    >
      {hasRiaOffer ? null : <ComparisonBanner isPrimaryEntity={isPrimaryEntity} />}
      <ComparisonHeader column={column} columnIndex={columnIndex} />
      {!hasRiaOffer ? (
        <div className="px-6 pt-6">
          <NumericLayout
            iconSize={19}
            reviewLink={marketplaceEntity.reviewLink}
            starRating={marketplaceEntity.starRating}
            starRatingTooltip={marketplaceEntity.starRatingTooltip}
            tooltipColor="var(--muted-foreground)"
            tooltipIconColor="var(--muted-foreground)"
          />
        </div>
      ) : null}
      <div className="mx-6 border-b border-border pt-3" />
      {driverIds.map(driverId => (
        <ComparisonDriver
          driver={column.drivers.find(candidate => candidate.id === driverId)}
          hasSeparator={driverId !== driverIds.at(-1)}
          key={driverId}
          label={getDriverLabel(columns, driverId)}
        />
      ))}
      <div className="px-6 pt-5 pb-6">
        {cta ? (
          <CtaButton
            analyticsPayload={analyticsPayload}
            cta={cta}
            federallyInsuredAnnotation={undefined}
            fullWidth
            size="dense"
            variant={isPrimaryEntity ? 'primary' : 'outlinePrimary'}
          />
        ) : null}
      </div>
      {hasRiaOffer ? (
        <div className="px-6 pb-6">
          {riaDisclaimerText ? (
            <RiaDisclaimer
              hideToolTip={!isFidelityOffer}
              isFidelityOffer={isFidelityOffer}
              isReview
              monetizable={marketplaceEntity.monetizable}
              riaDisclaimerText={riaDisclaimerText}
            />
          ) : null}
        </div>
      ) : null}
    </AnalyticsSkuViewed>
  );
}

function ComparisonBanner({ isPrimaryEntity }: { isPrimaryEntity: boolean }) {
  return isPrimaryEntity ? (
    <div className="typography-text-small bg-secondary px-6">Current Product</div>
  ) : (
    <div className="min-h-[25px]" />
  );
}

interface ComparisonHeaderProps {
  column: BaseComparisonTableColumn;
  columnIndex: number;
}

function ComparisonHeader({ column, columnIndex }: ComparisonHeaderProps) {
  const { marketplaceEntity } = column;
  const analyticsPayload = {
    index: columnIndex,
    institutionId: marketplaceEntity.institution.id,
    offerId: marketplaceEntity.id,
    productId: marketplaceEntity.product.id,
    productType: marketplaceEntity.productType,
    monetizable: marketplaceEntity.monetizable,
  };
  const header = (
    <>
      <div
        className={`flex items-center justify-center px-6 pt-5 ${marketplaceEntity.image?.source ? 'pb-6' : ''}`}
      >
        {marketplaceEntity.image?.source ? (
          <>
            <div className="hidden justify-center md:flex">
              <Image
                alt={marketplaceEntity.image.alt ?? ''}
                height={85}
                src={marketplaceEntity.image.source}
                style={{ height: 'auto', maxWidth: '60%', width: 'auto' }}
                width={85}
              />
            </div>
            <div className="flex justify-center md:hidden">
              <Image
                alt={marketplaceEntity.image.alt ?? ''}
                height={85}
                src={marketplaceEntity.image.source}
                style={{ height: 'auto', maxWidth: '80%', width: 'auto' }}
                width={85}
              />
            </div>
          </>
        ) : null}
      </div>
      <span className="typography-text-small px-6 font-bold text-secondary-foreground hover:underline">
        {marketplaceEntity.displayName}
      </span>
    </>
  );

  // Logo + name occupy two subgrid rows so names align regardless of logo height.
  const cellsClassName = 'row-span-2 grid grid-cols-1 grid-rows-subgrid';
  const cta = getColumnCta(marketplaceEntity);

  return cta ? (
    <TrackedLink
      analyticsPayload={analyticsPayload}
      cta={cta}
      className={`${cellsClassName} cursor-pointer no-underline`}
      linkType={LinkType.PRODUCT_IMAGE}
      monetizable={marketplaceEntity.monetizable}
    >
      {header}
    </TrackedLink>
  ) : (
    <div className={cellsClassName}>{header}</div>
  );
}

interface ComparisonDriverProps {
  driver: BaseComparisonTableColumn['drivers'][number] | undefined;
  hasSeparator: boolean;
  label: string;
}

function ComparisonDriver({ driver, hasSeparator, label }: ComparisonDriverProps) {
  return (
    <div className="flex flex-col gap-2 px-6 py-4">
      {label ? <span className="typography-text-small text-muted-foreground">{label}</span> : null}
      <div className="flex items-center gap-2">
        <span className="font-sans text-base">{driver?.value || 'N/A'}</span>
        {driver?.valueTooltip ? (
          <DisclosureTooltip
            className="max-w-[275px]"
            trigger={
              <button
                aria-label={`More information about ${label}`}
                className="cursor-pointer bg-transparent"
                type="button"
              >
                <CircleHelp aria-hidden size={16} />
              </button>
            }
          >
            <span className="typography-text-small">{driver.valueTooltip}</span>
          </DisclosureTooltip>
        ) : null}
      </div>
      {driver?.description ? (
        <span className="typography-text-small text-muted-foreground">{driver.description}</span>
      ) : null}
      {hasSeparator ? (
        <div className="comparison-driver-separator mt-auto border-b border-border" />
      ) : null}
    </div>
  );
}

function ReviewComparisonEditorialDisclaimer() {
  return (
    <div className="border-b-4 border-[var(--content-blue-lighter)] bg-[var(--content-blue-lightest)] p-2">
      <p className="typography-body-small leading-5 text-muted-foreground">
        NerdWallet doesn&apos;t invest its money with this provider, but they are our referral
        partner - so we get paid only if you click through and take a qualifying action (such as
        open an account with or provide your contact information to the provider). Most importantly,
        our reviews and ratings are objective and are never impacted by our partnerships. Our
        opinions are our own. Here is a list{' '}
        <ContentLink
          href={`${env.NEXT_PUBLIC_NW_URL}/p/our-partners`}
          rel="noopener"
          target="_blank"
        >
          of our partners
        </ContentLink>{' '}
        and{' '}
        <ContentLink
          href={`${env.NEXT_PUBLIC_NW_URL}/p/advertiser-disclosure`}
          rel="noopener"
          target="_blank"
        >
          here&apos;s how we make money
        </ContentLink>
        .
      </p>
    </div>
  );
}

interface ColumnBorderClassNameOptions {
  columnIndex: number;
  isPrimaryEntity: boolean;
  hasRiaOffer: boolean;
}

function getColumnBorderClassName({
  columnIndex,
  isPrimaryEntity,
  hasRiaOffer,
}: ColumnBorderClassNameOptions) {
  if (!hasRiaOffer && isPrimaryEntity) {
    return 'border-2 border-primary';
  }

  return [
    columnIndex === 0 ? 'border-l border-border' : '',
    'border-r border-b border-border',
    // RIA tables get the full-width ADVERTISEMENT bar as their top edge
    hasRiaOffer ? '' : 'border-t',
  ].join(' ');
}

// Matches VE InvestingComparisonTable Cta/OfferLogo: non-monetizing offers link to their review.
function getColumnCta(
  marketplaceEntity: BaseComparisonTableColumn['marketplaceEntity'],
): CtaType | undefined {
  if (marketplaceEntity.isMonetizing) {
    return marketplaceEntity.cta?.link ? marketplaceEntity.cta : undefined;
  }
  return marketplaceEntity.reviewLink
    ? {
        link: marketplaceEntity.reviewLink,
        showIcon: false,
        text: 'Read review',
        type: EntityPlacementCtaTypeEnum.ReadReview,
      }
    : undefined;
}

function isRiaOffer(column: BaseComparisonTableColumn) {
  return 'isRiaOffer' in column.marketplaceEntity && column.marketplaceEntity.isRiaOffer;
}

function getRiaDisclaimerText(column: BaseComparisonTableColumn) {
  return 'riaDisclaimerText' in column.marketplaceEntity
    ? column.marketplaceEntity.riaDisclaimerText
    : undefined;
}

function getIsFidelityOffer(column: BaseComparisonTableColumn) {
  return 'isFidelityOffer' in column.marketplaceEntity
    ? column.marketplaceEntity.isFidelityOffer
    : false;
}

function getDriverLabel(columns: BaseComparisonTableColumn[], driverId: string) {
  return (
    columns.flatMap(column => column.drivers).find(driver => driver.id === driverId)?.label ??
    driverId
  );
}
